import React, { useState, useEffect, useRef } from 'react';
import useGsn from '../../hooks/useGsn';
import * as api from '../../api';
import * as crypto from '../../crypto';

const DecryptedGsnImage = ({ attachmentJson }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    let active = true;
    let createdUrl = "";
    const load = async () => {
      try {
        const att = JSON.parse(attachmentJson);
        const blob = await api.downloadFileAttachment(att.fileId);
        const decrypted = await crypto.decryptFileSymmetric(blob, att.keyHex, att.ivHex);
        if (active) {
          createdUrl = URL.createObjectURL(decrypted);
          setImgUrl(createdUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to decrypt GSN image:", err);
        if (active) {
          setError(true);
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachmentJson]);

  if (loading) {
    return (
      <div className="gsn-img-placeholder" style={{ padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
        ⏳ Bild wird entschlüsselt...
      </div>
    );
  }
  if (error) {
    return <div style={{ color: 'var(--danger)', fontSize: '0.8rem', padding: '8px' }}>⚠️ Bild konnte nicht entschlüsselt werden.</div>;
  }

  return (
    <>
      <img
        src={imgUrl}
        alt="GSN Beitrag"
        className="gsn-post-img"
        style={{ cursor: 'zoom-in', maxWidth: '100%', borderRadius: '8px', marginTop: '8px' }}
        onClick={() => setIsZoomed(true)}
      />
      {isZoomed && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out'
          }}
          onClick={() => setIsZoomed(false)}
        >
          <img
            src={imgUrl}
            alt="GSN Beitrag vergrößert"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              border: '2px solid rgba(255, 255, 255, 0.1)'
            }}
          />
        </div>
      )}
    </>
  );
};

export default function GsnPane({ activeIdentity, derivedKeys, triggerAlert, setMobileMenuOpen, t, showConfirm }) {
  const {
    nodeFeed,
    followingFeed,
    loadingNodeFeed,
    loadingFollowingFeed,
    activeProfile,
    loadingProfile,
    activeComments,
    loadingComments,
    myProfile,
    fetchNodeFeed,
    fetchFollowingFeed,
    createPost,
    deletePost,
    reactToPost,
    addComment,
    deleteComment,
    fetchComments,
    followUser,
    unfollowUser,
    fetchProfile,
    updateProfile,
    setActiveProfile
  } = useGsn({ activeIdentity, derivedKeys, triggerAlert });

  const [activeTab, setActiveTab] = useState('node'); // 'node' | 'following'
  const [postBody, setPostBody] = useState('');
  const [repostOfPostId, setRepostOfPostId] = useState('');
  
  // Image attachments state
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [uploadedImageMeta, setUploadedImageMeta] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');

  // Emojis popover state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojis = ['😀', '😂', '😍', '👍', '❤️', '🔥', '👀', '✨', '🎉', '🚀'];

  // Comments state
  const [expandedComments, setExpandedComments] = useState({}); // postId -> boolean
  const [commentTextMap, setCommentTextMap] = useState({}); // postId -> string

  // Profile Edit fields
  const [editMode, setEditMode] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editAvatar, setEditAvatar] = useState('');
  const [editWebsite, setEditWebsite] = useState('');

  // Abuse Report modal state
  const [reportModalTarget, setReportModalTarget] = useState(null); // { type: 'post'|'user', id: string } | null
  const [reportCategory, setReportCategory] = useState('spam');
  const [reportSeverity, setReportSeverity] = useState('low');
  const [reportComment, setReportComment] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  // Fetch feeds on mount
  useEffect(() => {
    fetchNodeFeed();
    fetchFollowingFeed();
  }, [fetchNodeFeed, fetchFollowingFeed]);

  const currentFeed = activeTab === 'node' ? nodeFeed : followingFeed;
  const currentFeedLoading = activeTab === 'node' ? loadingNodeFeed : loadingFollowingFeed;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'node') {
      fetchNodeFeed();
    } else {
      fetchFollowingFeed();
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Create local preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreviewUrl(previewUrl);
    
    setUploadingImage(true);
    setImageUploadProgress(5);
    try {
      // strip metadata
      const cleanFile = await crypto.stripImageMetadata(file);
      setImageUploadProgress(10);
      
      // encrypt symmetrically
      const { encryptedBlob, keyHex, ivHex } = await crypto.encryptFileSymmetric(cleanFile);
      const encryptedSize = encryptedBlob.size;
      const encryptedBuf = await encryptedBlob.arrayBuffer();
      
      // compute hash
      const hashBuf = await window.crypto.subtle.digest('SHA-256', encryptedBuf);
      const fileHash = Array.prototype.map.call(new Uint8Array(hashBuf), x => ('00' + x.toString(16)).slice(-2)).join('');
      setImageUploadProgress(15);
      
      // init upload
      const initRes = await api.initUpload(file.name, encryptedSize, file.type, fileHash);
      const fileId = initRes.fileId;
      
      // chunk upload (1MB chunk size)
      const CHUNK_SIZE = 1024 * 1024;
      const totalChunks = Math.ceil(encryptedSize / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, encryptedSize);
        const chunkBlob = encryptedBlob.slice(start, end);
        const chunkBuf = await chunkBlob.arrayBuffer();
        
        const chunkHashBuf = await window.crypto.subtle.digest('SHA-256', chunkBuf);
        const chunkHash = Array.prototype.map.call(new Uint8Array(chunkHashBuf), x => ('00' + x.toString(16)).slice(-2)).join('');
        
        await api.uploadChunk(fileId, i, chunkHash, chunkBlob);
        setImageUploadProgress(Math.round(20 + ((i + 1) / totalChunks) * 80));
      }
      
      await api.completeUpload(fileId);
      setImageUploadProgress(100);
      
      setUploadedImageMeta({
        fileId,
        fileName: file.name,
        fileSize: encryptedSize,
        mimeType: file.type,
        keyHex,
        ivHex
      });
      
      triggerAlert(t('success') || 'Erfolg', t('gsn_image_uploaded') || 'Bild wurde verschlüsselt und hochgeladen.');
    } catch (err) {
      console.error(err);
      URL.revokeObjectURL(previewUrl);
      setImagePreviewUrl('');
      triggerAlert(t('error') || 'Fehler', (t('gsn_upload_failed') || 'Bild-Upload fehlgeschlagen: ') + err.message, 'danger');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!postBody.trim() && !uploadedImageMeta) return;

    let imageAttachmentStr = '';
    if (uploadedImageMeta) {
      imageAttachmentStr = JSON.stringify({
        fileId: uploadedImageMeta.fileId,
        keyHex: uploadedImageMeta.keyHex,
        ivHex: uploadedImageMeta.ivHex,
        fileName: uploadedImageMeta.fileName
      });
    }

    try {
      await createPost(postBody, imageAttachmentStr, repostOfPostId);
      setPostBody('');
      setRepostOfPostId('');
      setUploadedImageMeta(null);
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
        setImagePreviewUrl('');
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      triggerAlert(t('success') || 'Erfolg', t('gsn_post_created') || 'Beitrag erfolgreich geteilt.');
    } catch (err) {
      triggerAlert(t('error') || 'Fehler', err.message, 'danger');
    }
  };

  const handleDeletePost = (postId) => {
    showConfirm(
      t('gsn_delete_confirm_title') || 'Beitrag löschen?',
      t('gsn_delete_confirm_desc') || 'Bist du sicher, dass du diesen Beitrag unwiderruflich löschen möchtest?',
      async () => {
        try {
          await deletePost(postId);
        } catch (err) {
          triggerAlert(t('error') || 'Fehler', err.message, 'danger');
        }
      }
    );
  };

  const handleDeleteComment = (postId, commentId) => {
    showConfirm(
      t('gsn_delete_comment_confirm_title') || 'Kommentar löschen?',
      t('gsn_delete_comment_confirm_desc') || 'Bist du sicher, dass du diesen Kommentar unwiderruflich löschen möchtest?',
      async () => {
        try {
          await deleteComment(postId, commentId);
        } catch (err) {
          triggerAlert(t('error') || 'Fehler', err.message, 'danger');
        }
      }
    );
  };

  const handleRepost = (post) => {
    setRepostOfPostId(post.id);
    setPostBody(`Repost: "${post.body.slice(0, 50)}${post.body.length > 50 ? '...' : ''}"`);
    // Scroll to composer
    const composer = document.querySelector('.gsn-composer');
    if (composer) {
      composer.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleComments = (postId) => {
    const isExpanded = !expandedComments[postId];
    setExpandedComments(prev => ({ ...prev, [postId]: isExpanded }));
    if (isExpanded && !activeComments[postId]) {
      fetchComments(postId);
    }
  };

  const handleAddComment = async (postId) => {
    const text = commentTextMap[postId] || '';
    if (!text.trim()) return;

    try {
      await addComment(postId, text);
      setCommentTextMap(prev => ({ ...prev, [postId]: '' }));
    } catch (err) {
      triggerAlert(t('error') || 'Fehler', err.message, 'danger');
    }
  };

  const handleOpenProfile = async (gaiaId) => {
    await fetchProfile(gaiaId);
    setEditMode(false);
  };

  const handleSaveProfile = async () => {
    try {
      await updateProfile({
        displayName: editDisplayName,
        description: editBio,
        avatar: editAvatar,
        website: editWebsite
      });
      setEditMode(false);
    } catch (err) {
      triggerAlert(t('error') || 'Fehler', err.message, 'danger');
    }
  };

  const openEditProfile = () => {
    if (activeProfile) {
      setEditDisplayName(activeProfile.displayName || '');
      setEditBio(activeProfile.description || '');
      setEditAvatar(activeProfile.avatar || '');
      setEditWebsite(activeProfile.website || '');
      setEditMode(true);
    }
  };

  const handleOpenReportModal = (type, id) => {
    setReportModalTarget({ type, id });
    setReportCategory('spam');
    setReportSeverity('low');
    setReportComment('');
  };

  const handleSubmitReport = async () => {
    if (!reportModalTarget || !activeIdentity) return;
    setSubmittingReport(true);
    try {
      await api.submitAbuseReport(
        activeIdentity.ID,
        reportModalTarget.type,
        reportModalTarget.id,
        reportCategory,
        reportSeverity,
        null,
        reportComment
      );
      triggerAlert(t('success') || 'Erfolg', t('gsn_report_submitted') || 'Meldung wurde an die Governance übermittelt.');
      setReportModalTarget(null);
    } catch (err) {
      triggerAlert(t('error') || 'Fehler', err.message, 'danger');
    } finally {
      setSubmittingReport(false);
    }
  };

  const renderPostAvatar = (displayName, avatarHex) => {
    const initial = displayName ? displayName.charAt(0).toUpperCase() : '?';
    // Generate simple seed color from display name
    let hash = 0;
    for (let i = 0; i < (displayName || '').length; i++) {
      hash = (displayName || '').charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = `hsl(${hash % 360}, 50%, 45%)`;

    return (
      <div className="gsn-avatar" style={{ backgroundColor: color }}>
        {avatarHex && avatarHex.length <= 4 ? avatarHex : initial}
      </div>
    );
  };

  return (
    <div className="gsn-container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="gsn-header" style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button type="button" className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)}>
            {t('menu') || 'Menü'}
          </button>
          <h2 style={{ margin: 0, fontWeight: 800, fontSize: '1.4rem', color: 'var(--text-primary)' }}>
            🌐 {t('gsn_title') || 'GSN Social Feed'}
          </h2>
        </div>
        <div className="gsn-tabs">
          <button
            type="button"
            className={`gsn-tab ${activeTab === 'node' ? 'active' : ''}`}
            onClick={() => handleTabChange('node')}
          >
            🏠 {t('gsn_node_feed') || 'Node Feed'}
          </button>
          <button
            type="button"
            className={`gsn-tab ${activeTab === 'following' ? 'active' : ''}`}
            onClick={() => handleTabChange('following')}
          >
            👥 {t('gsn_following_feed') || 'Folge ich'}
          </button>
        </div>
      </div>

      {/* Feed List (Now on Top) */}
      <div className="gsn-feed-list" style={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', paddingRight: '4px', marginBottom: '20px' }}>
        {currentFeedLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            <div className="spinner" style={{ border: '3px solid rgba(0,0,0,0.1)', borderTop: '3px solid var(--accent-cyan)', borderRadius: '50%', width: '30px', height: '30px', animation: 'spin 1s linear infinite', margin: '0 auto 10px auto' }}></div>
            {t('loading') || 'Lade Feed...'}
          </div>
        ) : currentFeed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            📭 {t('gsn_feed_empty') || 'Keine Beiträge gefunden.'}
          </div>
        ) : (
          currentFeed.map(post => {
            const isMyPost = activeIdentity && activeIdentity.GaiaID && post.gaiaId && post.gaiaId.toLowerCase() === activeIdentity.GaiaID.toLowerCase();
            const isOperator = myProfile?.isVerifiedOperator === true;
            const hasImage = !!post.imageAttachment;
            const referencedPost = post.repostOfPostId ? currentFeed.find(p => p.id === post.repostOfPostId) : null;

            return (
              <div key={post.id} className="gsn-post-card">
                {/* Header */}
                <div className="gsn-post-header">
                  <div onClick={() => handleOpenProfile(post.gaiaId)}>
                    {renderPostAvatar(post.displayName, post.avatar)}
                  </div>
                  <div className="gsn-post-meta">
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="gsn-post-author" onClick={() => handleOpenProfile(post.gaiaId)}>
                        {post.displayName || post.gaiaId}
                      </span>
                      <div className="gsn-badges">
                        {post.isVerifiedOperator && (
                          <span className="gsn-badge gsn-badge-op" title="Node Operator">⭐ Operator</span>
                        )}
                        {post.isVerifiedGovernance && (
                          <span className="gsn-badge gsn-badge-gov" title="Governance Board">🛡️ Gov</span>
                        )}
                        {post.isVerifiedPassport && (
                          <span className="gsn-badge gsn-badge-pass" title="Verifizierter Passport">💎 Passport</span>
                        )}
                      </div>
                    </div>
                    <span className="gsn-post-gaiaid" onClick={() => handleOpenProfile(post.gaiaId)}>
                      {post.gaiaId}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {new Date(post.timestamp).toLocaleString()}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      className="btn-action"
                      style={{ padding: '4px', fontSize: '0.8rem' }}
                      title={t('gsn_report_post') || 'Beitrag melden'}
                      onClick={() => handleOpenReportModal('post', post.id)}
                    >
                      🚩
                    </button>
                    {(isMyPost || isOperator) && (
                      <button
                        type="button"
                        className="btn-action"
                        style={{ padding: '4px', fontSize: '0.8rem', color: 'var(--danger)' }}
                        title={t('delete') || 'Löschen'}
                        onClick={() => handleDeletePost(post.id)}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="gsn-post-body">
                  {post.body}
                </div>

                {/* Render Encrypted Image */}
                {hasImage && (
                  <DecryptedGsnImage attachmentJson={post.imageAttachment} />
                )}

                {/* Repost content */}
                {post.repostOfPostId && (
                  <div style={{ borderLeft: '3px solid var(--accent-cyan)', paddingLeft: '12px', marginLeft: '6px', background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '6px', marginTop: '8px' }}>
                    {referencedPost ? (
                      <div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.8rem', marginBottom: '4px' }}>
                          <span style={{ fontWeight: 700 }}>{referencedPost.displayName}</span>
                          <span style={{ color: 'var(--text-secondary)' }}>{referencedPost.gaiaId}</span>
                        </div>
                        <div style={{ fontSize: '0.85rem' }}>{referencedPost.body}</div>
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        🔄 Shared post: <code>{post.repostOfPostId.slice(0, 8)}...</code>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="gsn-reactions-row">
                  {['👍', '❤️', '🔥', '👀'].map(emoji => {
                    const reactionCount = (post.reactions && post.reactions[emoji]) || 0;
                    const didIReact = post.reactedByMe && post.reactedByMe[emoji] === true;
                    return (
                      <button
                        key={emoji}
                        type="button"
                        className={`gsn-action-btn ${didIReact ? 'active' : ''}`}
                        onClick={() => reactToPost(post.id, emoji)}
                      >
                        <span>{emoji}</span>
                        <span>{reactionCount}</span>
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    className={`gsn-action-btn ${expandedComments[post.id] ? 'active' : ''}`}
                    onClick={() => toggleComments(post.id)}
                  >
                    💬 {post.commentCount || 0}
                  </button>

                  <button
                    type="button"
                    className="gsn-action-btn"
                    onClick={() => handleRepost(post)}
                    title={t('gsn_repost') || 'Teilen / Reposten'}
                  >
                    🔄
                  </button>
                </div>

                {/* Collapsible Comments Section */}
                {expandedComments[post.id] && (
                  <div className="gsn-comments-section">
                    {loadingComments && !activeComments[post.id] ? (
                      <div style={{ textAlign: 'center', padding: '10px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        Lade Kommentare...
                      </div>
                    ) : (activeComments[post.id] || []).length === 0 ? (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', padding: '6px' }}>
                        Noch keine Kommentare. Schreib den ersten!
                      </div>
                    ) : (
                      (activeComments[post.id] || []).map(comment => {
                        const isMyComment = activeIdentity && activeIdentity.GaiaID && comment.gaiaId && comment.gaiaId.toLowerCase() === activeIdentity.GaiaID.toLowerCase();
                        return (
                          <div key={comment.id} className="gsn-comment-item">
                            {renderPostAvatar(comment.displayName, comment.avatar)}
                            <div className="gsn-comment-meta">
                              <div className="gsn-comment-header">
                                <span className="gsn-comment-author">{comment.displayName || comment.gaiaId}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span className="gsn-comment-time">{new Date(comment.timestamp).toLocaleTimeString()}</span>
                                  {(isMyComment || isMyPost || isOperator) && (
                                    <button
                                      type="button"
                                      className="btn-action"
                                      style={{ padding: '0 4px', fontSize: '0.75rem', color: 'var(--danger)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                                      title={t('delete') || 'Löschen'}
                                      onClick={() => handleDeleteComment(post.id, comment.id)}
                                    >
                                      🗑️
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="gsn-comment-body">{comment.body}</div>
                            </div>
                          </div>
                        );
                      })
                    )}

                    {activeIdentity && (
                      <div className="gsn-comment-composer">
                        <input
                          type="text"
                          className="gsn-comment-input"
                          placeholder={t('gsn_comment_placeholder') || 'Schreibe einen Kommentar...'}
                          value={commentTextMap[post.id] || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCommentTextMap(prev => ({ ...prev, [post.id]: val }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddComment(post.id);
                          }}
                        />
                        <button
                          type="button"
                          className="btn-primary"
                          style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                          onClick={() => handleAddComment(post.id)}
                        >
                          💬
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Composer (Now at Bottom) */}
      {activeIdentity && (
        <form className="gsn-composer" onSubmit={handleCreatePost} style={{ flexShrink: 0, margin: 0, padding: '12px 16px' }}>
          <textarea
            className="gsn-composer-textarea"
            placeholder={t('gsn_composer_placeholder') || 'Was gibt es Neues in der Föderation?'}
            value={postBody}
            onChange={(e) => setPostBody(e.target.value)}
            style={{ minHeight: '60px', marginBottom: '8px' }}
          />

          {repostOfPostId && (
            <div style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', fontSize: '0.8rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span>🔄 Reposting Beitrag: <code>{repostOfPostId.slice(0, 8)}...</code></span>
              <button type="button" className="btn-secondary" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={() => { setRepostOfPostId(''); setPostBody(''); }}>
                ✕
              </button>
            </div>
          )}

          {uploadedImageMeta && imagePreviewUrl && (
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '12px', marginBottom: '8px', background: 'rgba(0,0,0,0.25)', padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
              <img
                src={imagePreviewUrl}
                alt="Selected preview"
                style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--border-color)' }}
              />
              <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)', display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 'bold' }}>🔒 Encrypted</span>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{uploadedImageMeta.fileName}</span>
              </div>
              <button
                type="button"
                className="btn-action"
                style={{ padding: '4px 8px', fontSize: '0.8rem', color: 'var(--danger)' }}
                onClick={() => {
                  setUploadedImageMeta(null);
                  URL.revokeObjectURL(imagePreviewUrl);
                  setImagePreviewUrl('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                ✕
              </button>
            </div>
          )}

          {uploadingImage && (
            <div style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                <span>🔒 Verschlüsseln und Hochladen...</span>
                <span>{imageUploadProgress}%</span>
              </div>
              <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ width: `${imageUploadProgress}%`, height: '100%', background: 'var(--accent-cyan)' }}></div>
              </div>
            </div>
          )}

          <div className="gsn-composer-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                type="button"
                className="btn-action"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={uploadingImage}
                title={t('gsn_add_image') || 'Bild hinzufügen (Verschlüsselt)'}
                style={{ fontSize: '1.1rem', padding: '6px' }}
              >
                📷
              </button>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleImageUpload}
              />

              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="btn-action"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  title={t('gsn_add_emoji') || 'Emoji einfügen'}
                  style={{ fontSize: '1.1rem', padding: '6px' }}
                >
                  😀
                </button>
                {showEmojiPicker && (
                  <div className="glass-panel" style={{ position: 'absolute', bottom: '40px', left: 0, zIndex: 100, padding: '8px', display: 'flex', gap: '6px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-glass)' }}>
                    {emojis.map(e => (
                      <button
                        key={e}
                        type="button"
                        style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '4px' }}
                        onClick={() => {
                          setPostBody(prev => prev + e);
                          setShowEmojiPicker(false);
                        }}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={(!postBody.trim() && !uploadedImageMeta) || uploadingImage}
              style={{ padding: '6px 16px', fontSize: '0.85rem' }}
            >
              🚀 {t('gsn_post_btn') || 'Teilen'}
            </button>
          </div>
        </form>
      )}

      {/* Sliding Profile Drawer */}
      {activeProfile && (
        <div className="gsn-drawer">
          <div className="gsn-drawer-header">
            <h3 style={{ margin: 0, fontWeight: 800 }}>👤 {t('gsn_profile') || 'Benutzerprofil'}</h3>
            <button type="button" className="gsn-drawer-close" onClick={() => setActiveProfile(null)}>
              ✕
            </button>
          </div>

          <div className="gsn-drawer-content">
            {loadingProfile ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                Lade Profil...
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {!editMode ? (
                  <>
                    <div className="gsn-profile-header">
                      {renderPostAvatar(activeProfile.displayName, activeProfile.avatar)}
                      <div className="gsn-profile-name">{activeProfile.displayName || activeProfile.gaiaId}</div>
                      <div className="gsn-profile-gaiaid" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <code>{activeProfile.gaiaId}</code>
                        <button
                          type="button"
                          className="btn-action"
                          style={{ padding: '2px 6px', fontSize: '0.7rem' }}
                          onClick={() => {
                            navigator.clipboard.writeText(activeProfile.gaiaId);
                            triggerAlert('Kopiert', 'GaiaID wurde kopiert.');
                          }}
                        >
                          📋
                        </button>
                      </div>
                      
                      <div className="gsn-badges" style={{ justifyContent: 'center', marginTop: '6px' }}>
                        {activeProfile.isVerifiedOperator && (
                          <span className="gsn-badge gsn-badge-op">⭐ Node Operator</span>
                        )}
                        {activeProfile.isVerifiedGovernance && (
                          <span className="gsn-badge gsn-badge-gov">🛡️ Governance</span>
                        )}
                        {activeProfile.isVerifiedPassport && (
                          <span className="gsn-badge gsn-badge-pass">💎 Passport</span>
                        )}
                      </div>
                    </div>

                    <div className="gsn-profile-stats">
                      <div className="gsn-profile-stat">
                        <span className="gsn-profile-stat-val">{activeProfile.followersCount || 0}</span>
                        <span className="gsn-profile-stat-lbl">{t('gsn_followers') || 'Follower'}</span>
                      </div>
                      <div className="gsn-profile-stat">
                        <span className="gsn-profile-stat-val">{activeProfile.followingCount || 0}</span>
                        <span className="gsn-profile-stat-lbl">{t('gsn_following') || 'Folge ich'}</span>
                      </div>
                    </div>

                    {activeProfile.description && (
                      <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                        <strong>{t('gsn_bio') || 'Beschreibung / Bio:'}</strong>
                        <p style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>{activeProfile.description}</p>
                      </div>
                    )}

                    {activeProfile.website && (
                      <div style={{ fontSize: '0.9rem' }}>
                        🌐 <strong>Website:</strong>{' '}
                        <a href={activeProfile.website.startsWith('http') ? activeProfile.website : `https://${activeProfile.website}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-cyan)' }}>
                          {activeProfile.website}
                        </a>
                      </div>
                    )}

                    {activeProfile.trustPassportSummary && (
                      <div style={{ background: 'rgba(255, 0, 127, 0.03)', padding: '12px', borderRadius: '8px', fontSize: '0.8rem', border: '1px solid rgba(255, 0, 127, 0.2)' }}>
                        <strong style={{ color: '#ff007f', display: 'block', marginBottom: '6px' }}>💎 Trust Passport Nachweis</strong>
                        <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{activeProfile.trustPassportSummary}</div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      {activeIdentity && activeProfile.gaiaId.toLowerCase() !== activeIdentity.GaiaID.toLowerCase() ? (
                        <>
                          {activeProfile.isFollowing ? (
                            <button
                              type="button"
                              className="btn-primary"
                              style={{ flex: 1, background: 'rgba(255,255,255,0.1)', color: '#fff', borderColor: 'var(--border-color)' }}
                              onClick={() => unfollowUser(activeProfile.gaiaId)}
                            >
                              🤝 {t('gsn_unfollow') || 'Entfolgen'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn-primary"
                              style={{ flex: 1 }}
                              onClick={() => followUser(activeProfile.gaiaId)}
                            >
                              ➕ {t('gsn_follow') || 'Folgen'}
                            </button>
                          )}

                          <button
                            type="button"
                            className="btn-secondary"
                            style={{ background: 'var(--danger-glow)', color: 'var(--danger)', borderColor: 'var(--danger)' }}
                            onClick={() => handleOpenReportModal('user', activeProfile.gaiaId)}
                          >
                            🚩 {t('gsn_report') || 'Melden'}
                          </button>
                        </>
                      ) : activeIdentity && (
                        <button
                          type="button"
                          className="btn-primary"
                          style={{ flex: 1 }}
                          onClick={openEditProfile}
                        >
                          ⚙️ {t('gsn_edit_profile') || 'Profil bearbeiten'}
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  // Edit Mode
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <h4 style={{ margin: 0 }}>⚙️ {t('gsn_edit_profile') || 'Profil bearbeiten'}</h4>
                    
                    <div>
                      <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                        {t('gsn_display_name') || 'Anzeigename'}
                      </label>
                      <input
                        type="text"
                        className="gsn-comment-input"
                        style={{ width: '100%', boxSizing: 'border-box' }}
                        value={editDisplayName}
                        onChange={(e) => setEditDisplayName(e.target.value)}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                        {t('gsn_avatar_emoji') || 'Avatar (Emoji)'}
                      </label>
                      <input
                        type="text"
                        className="gsn-comment-input"
                        style={{ width: '100%', boxSizing: 'border-box' }}
                        maxLength={2}
                        value={editAvatar}
                        onChange={(e) => setEditAvatar(e.target.value)}
                        placeholder="z.B. 🚀 oder 🐼"
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                        {t('gsn_bio_label') || 'Beschreibung / Bio'}
                      </label>
                      <textarea
                        className="gsn-composer-textarea"
                        style={{ width: '100%', minHeight: '80px', boxSizing: 'border-box', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '6px', background: 'rgba(255,255,255,0.05)', color: 'var(--text-primary)' }}
                        value={editBio}
                        onChange={(e) => setEditBio(e.target.value)}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                        Website
                      </label>
                      <input
                        type="text"
                        className="gsn-comment-input"
                        style={{ width: '100%', boxSizing: 'border-box' }}
                        value={editWebsite}
                        onChange={(e) => setEditWebsite(e.target.value)}
                        placeholder="https://example.com"
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ flex: 1 }}
                        onClick={() => setEditMode(false)}
                      >
                        {t('cancel') || 'Abbrechen'}
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        style={{ flex: 1 }}
                        onClick={handleSaveProfile}
                      >
                        {t('gsn_save_profile') || 'Speichern'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Abuse Report Modal Overlay */}
      {reportModalTarget && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div className="glass-panel" style={{ width: '100%', maxWidth: '400px', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', background: 'var(--card-bg)' }}>
            <h3 style={{ margin: '0 0 16px 0', fontWeight: 800 }}>🚩 {t('gsn_report_title') || 'Verstoß melden'}</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                  {t('gsn_report_category') || 'Kategorie'}
                </label>
                <select
                  value={reportCategory}
                  onChange={(e) => setReportCategory(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', background: 'rgba(0,0,0,0.3)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  <option value="spam">{t('gsn_report_spam') || 'Spam / Werbung'}</option>
                  <option value="harassment">{t('gsn_report_harassment') || 'Belästigung / Hassrede'}</option>
                  <option value="abuse">{t('gsn_report_abuse') || 'Missbrauch'}</option>
                  <option value="illegal">{t('gsn_report_illegal') || 'Illegale Inhalte'}</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                  {t('gsn_report_severity') || 'Dringlichkeit'}
                </label>
                <select
                  value={reportSeverity}
                  onChange={(e) => setReportSeverity(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', background: 'rgba(0,0,0,0.3)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  <option value="low">{t('gsn_report_low') || 'Niedrig'}</option>
                  <option value="medium">{t('gsn_report_medium') || 'Mittel'}</option>
                  <option value="high">{t('gsn_report_high') || 'Hoch'}</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                  {t('gsn_report_comment') || 'Zusätzlicher Kommentar'}
                </label>
                <textarea
                  style={{ width: '100%', height: '80px', boxSizing: 'border-box', padding: '8px', borderRadius: '6px', background: 'rgba(0,0,0,0.3)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', resize: 'vertical' }}
                  placeholder="Warum meldest du diesen Inhalt?"
                  value={reportComment}
                  onChange={(e) => setReportComment(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ flex: 1 }}
                  onClick={() => setReportModalTarget(null)}
                  disabled={submittingReport}
                >
                  {t('cancel') || 'Abbrechen'}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' }}
                  onClick={handleSubmitReport}
                  disabled={submittingReport}
                >
                  {submittingReport ? 'Sendet...' : (t('gsn_report_submit') || 'Melden')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
